import type { CSSProperties, FunctionComponent } from "preact";
import * as stylex from "@stylexjs/stylex";

const spin = stylex.keyframes({
    "0%": {
        transform: "rotate(0deg)",
    },
    "100%": {
        transform: "rotate(360deg)",
    },
});

const dots = stylex.keyframes({
    "0%": {
        content: ".\\00a0\\00a0",
    },
    "33%": {
        content: "..\\00a0",
    },
    "66%": {
        content: "...",
    },
});

const styles = stylex.create({
    spinner: {
        margin: "5px",
        animationName: spin,
        animationDuration: "2s",
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
    },
    loader: {
        gap: "2px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter Variable', sans-serif",
        fontSize: "10px",
    },
    loaderText: {
        "::after": {
            content: "\\00a0\\00a0\\00a0",
            display: "inline-flex",
            animationName: dots,
            animationDuration: "1s",
            animationIterationCount: "infinite",
        },
    },
    loaderFullHeight: {
        height: "100%",
    },
});

interface SpinnerProps {
    size?: number;
    color?: string;
}

const Circle: FunctionComponent<SpinnerProps> = ({ size = 32, color = "black" }) => {
    return (
        <svg {...stylex.props(styles.spinner)} viewBox="0 0 205 205" style={`width: ${size}px;`}>
            <path
                fill={color}
                stroke={color}
                stroke-width="4"
                d="M149 111q2.7-.5 4.4 2c9 15.4 17.6 46.5-7.3 71.7-23.5 23.7-59.6 19.6-84.2 8.6a4 4 0 0 1-2.6-4.4 4 4
                     0 0 1 3.7-3.5 113 113 0 0 0 51.7-17.1c16.4-11 29.4-28 31.1-53.7a4 4 0 0 1 3.2-3.7ZM5.8 87.1a4 4 0 0
                     1 4.5 2.3A113 113 0 0 0 43.2 133a68 68 0 0 0 60.9 12 4 4 0 0 1 4.4 1.9 4 4 0 0 1-.5 4.8c-11.7
                     13.6-38.3 31.7-70.2 16.2C7.8 153.4 0 118 2.4 91.1q.5-3.2 3.4-4Zm121.8-28c17.1-5 49.3-5.6 67.7 24.7
                     17.3 28.5 4.4 62.5-12.2 83.7a4 4 0 0 1-5 1.4 4 4 0 0 1-2.4-4.4 113 113 0 0 0-4-54.4A67 67 0 0 0
                     127.2 67a4 4 0 0 1-2.7-4 4 4 0 0 1 3-3.8Zm-44.2-57c2-.6 3.9.3 4.8 1.8a4 4 0 0 1-.8 5A113 113 0 0 0
                     57 54.4c-6.2 18.7-6 40 8.4 61.5a4 4 0 0 1-.3 4.8 4 4 0 0 1-4.7 1c-16.6-6.5-42.4-25.9-38.2-61 4-33.2
                     35-52 61.2-58.5Zm37.5 3.7c33-5.1 59.6 19.7 72.8 43.1 1 1.8.7 3.8-.5 5.1a4 4 0 0 1-5 .6 113 113 0 0
                     0-51.8-17c-19.7-.9-40.2 5-57 24.7a4 4 0 0 1-4.7 1 4 4 0 0 1-2.3-4.2C74.4 41.4 86 11.4 121 5.9Z"
            />
        </svg>
    );
};

const Loader: FunctionComponent<
    SpinnerProps & { label: string; fullHeight?: boolean; style?: CSSProperties }
> = ({ label, size = 32, style = {}, color = "black", fullHeight = false }) => {
    return (
        <div {...stylex.props(styles.loader, fullHeight && styles.loaderFullHeight)} style={style}>
            <Circle size={size} color={color} />
            <span {...stylex.props(styles.loaderText)}>{label}</span>
        </div>
    );
};

export default { Circle, Loader };
